/**
 * @fileoverview Shared inspection template General tab sections.
 * @module template-builder/tabs
 *
 * Responsibilities:
 * - Render shared identity and classification fields for template editors.
 * - Provide reusable tenant/system slot sections for appearance, parameters, reinspection, library settings, and AI metadata.
 * - Keep host pages as composition shells while persistence remains injected through callbacks.
 *
 * Dependencies:
 * - React state for local section UI.
 * - lucide-react icons and the shared ui-admin IconPicker.
 * - Canonical template builder types from ui-admin.
 *
 * Architecture:
 * - Presentation-only shared editor surface; reads and writes are supplied by local-first host services.
 * - No API, fetch, storage, or mock-data access is performed by this component.
 *
 * Notes:
 * - System-specific and tenant-specific sections are composed through slots to avoid divergent page-level General tabs.
 */
import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { AlertTriangle, Building2, Check, CheckCircle2, ChevronDown, ChevronUp, Globe, Info, Lock, Pencil, Plus, Sparkles, Tag, X, XCircle } from 'lucide-react';
import { analyzeBulkTemplateReadiness, validateBulkDefaults } from '@firesquirrel/core-domain';
import type { BulkAnalysisForm, BulkAwarenessItem, BulkTemplateAnalysisResult, BulkDefaultsValidationResult } from '@firesquirrel/core-domain';
import { IconPicker } from '../../IconPicker';
import { ToastNotification, useToastNotification } from '../../ui/ToastNotification';
import type { TemplateBuilderTemplate, ReinspectionScheduleEntry, TemplateParameters, TemplateType } from '../../../types/templateTypes';
import { TEMPLATE_TYPE_LABELS } from '../../../types/templateTypes';

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Emits template/parameter patches to host pages, which persist through Dexie-backed services.
 *
 * Sync Strategy:
 * - Background sync is owned by the injected host service callbacks.
 *
 * Conflict Resolution:
 * - Host services and replication metadata own conflict handling; this file is presentation-only.
 */

type TemplateLibrarySettings = {
  isInLibrary?: boolean;
  visibility?: 'public' | 'org' | 'private' | string;
  tags?: string[];
  _aiRefresh?: number;
};

const TEMPLATE_TYPE_DESCRIPTIONS: Record<TemplateType, string> = {
  wildland: 'For wildland-urban interface defensible space inspections',
  fireCode: 'For fire and life safety inspection workflows',
  hydrant: 'For hydrant inspection and maintenance workflows',
  complex: 'For multi-occupancy and complex site inspection workflows',
  communityRisk: 'For community risk reduction and outreach workflows',
  custom: 'Custom inspection type',
};

const ICON_COLOR_PRESETS = [
  '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2',
  '#2563eb', '#7c3aed', '#c026d3', '#e11d48', '#475569',
  '#0d9488', '#4f46e5',
];

/**
 * @contract TemplateGeneralTabProps
 *
 * Purpose:
 * - Defines the shared General tab composition contract used by system-web and tenant-web.
 *
 * Fields:
 * - data: current template record rendered by the tab.
 * - scope: host application scope for layout and policy-specific copy.
 * - onSave: host-provided local-first template patch callback.
 * - slots: optional host-specific React sections composed around shared identity/classification fields.
 *
 * Rules:
 * - Callers own validation, persistence, Dexie subscriptions, and sync status.
 * - The component must not call APIs or own production data access.
 *
 * Sync:
 * - Mutations flow through the injected callback and the host's Dexie-backed service.
 */
export interface TemplateGeneralTabProps {
  data: TemplateBuilderTemplate;
  isOffline?: boolean;
  onSave: (patch: Partial<TemplateBuilderTemplate>) => void | Promise<void>;
  validationSchema?: unknown;
  scope: 'system' | 'tenant';
  /** When provided, restricts template type dropdown to these types only.
   *  When omitted, all TEMPLATE_TYPE_LABELS keys are shown (system-web default). */
  enabledTemplateTypes?: TemplateType[];
  /** Forms belonging to this template — used to compute the bulk awareness summary. */
  forms?: ReadonlyArray<BulkAnalysisForm>;
  iconSlot?: ReactNode;
  parametersSlot?: ReactNode;
  reinspectionSlot?: ReactNode;
  librarySlot?: ReactNode;
  aiMetadataSlot?: ReactNode;
  shareSlot?: ReactNode;
  className?: string;
}

interface TemplateSectionProps {
  title: string;
  children: ReactNode;
  className?: string;
}

function TemplateSection({ title, children, className }: TemplateSectionProps) {
  return (
    <div className={className}>
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{title}</h3>
      {children}
    </div>
  );
}

/**
 * @component TemplateGeneralTab
 *
 * Purpose:
 * - Provides the canonical shared General tab wrapper for inspection template editors.
 *
 * Props:
 * - data: current template record.
 * - scope: tenant or system layout policy.
 * - onSave: local-first template patch callback supplied by the host page.
 * - slots: optional host-specific sections for appearance, parameters, reinspection, library, AI, and sharing.
 *
 * Behavior:
 * - Renders common identity and classification controls and composes scope-specific sections.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not call APIs, fetch data, generate mock records, or persist directly.
 *
 * Performance:
 * - Stateless except for child slots; callers control memoization as needed.
 *
 * @reusable
 *
 * Usage:
 * - System-web and tenant-web template editor pages.
 *
 * Rules:
 * - Presentation-only; persistence is injected through callbacks.
 */
export function TemplateGeneralTab({
  data,
  onSave,
  scope,
  enabledTemplateTypes,
  forms,
  iconSlot,
  parametersSlot,
  reinspectionSlot,
  librarySlot,
  aiMetadataSlot,
  shareSlot,
  className,
}: TemplateGeneralTabProps) {
  const templateTypeOptions: [TemplateType, string][] = enabledTemplateTypes
    ? enabledTemplateTypes.map((t) => [t, TEMPLATE_TYPE_LABELS[t]])
    : (Object.entries(TEMPLATE_TYPE_LABELS) as [TemplateType, string][]);
  const identitySection = (
    <TemplateSection title="Identity" className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Template Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          required
          maxLength={250}
          value={data.name}
          onChange={(event) => onSave({ name: event.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--brand-primary) focus:border-transparent"
          placeholder={scope === 'tenant' ? 'e.g. Defensible Space Inspection' : 'Template name'}
        />
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-400">Template name is required</p>
          <p className="text-xs text-gray-400">{data.name.length}/250</p>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={data.description ?? ''}
          onChange={(event) => onSave({ description: event.target.value })}
          rows={scope === 'tenant' ? 4 : 3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--brand-primary) focus:border-transparent resize-none"
          placeholder={scope === 'tenant' ? 'Describe what this template is used for…' : "Brief description of this template's purpose..."}
        />
      </div>
    </TemplateSection>
  );

  const classificationSection = (
    <TemplateSection title="Classification">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Template Type <span className="text-red-500">*</span>
        </label>
        <select
          value={data.templateType}
          aria-label="Template type"
          onChange={(event) => {
            const templateType = event.target.value as TemplateType;
            onSave({ templateType, templateTypeName: TEMPLATE_TYPE_LABELS[templateType] });
          }}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--brand-primary) bg-white"
        >
          {templateTypeOptions.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
          <Info size={11} /> {TEMPLATE_TYPE_DESCRIPTIONS[data.templateType]}
        </p>
      </div>
    </TemplateSection>
  );

  if (scope === 'system') {
    return (
      <div className={className ?? 'max-w-2xl space-y-6 py-6 px-6'}>
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-2">Template Details</h3>
          {identitySection}
          {classificationSection}
        </div>
        {librarySlot}
        {aiMetadataSlot}
        {shareSlot}
      </div>
    );
  }

  return (
    <div className={className ?? 'p-5 max-w-7xl'}>
      <div className="grid grid-cols-3 gap-x-6 gap-y-5 items-start">
        {identitySection}
        <div className="space-y-4">
          {classificationSection}
          {iconSlot && <hr className="border-gray-200" />}
          {iconSlot}
        </div>
        {parametersSlot}
        {reinspectionSlot}
        <BulkInspectionSection template={data} forms={forms} onSave={onSave} />
        {librarySlot}
        {aiMetadataSlot}
        {shareSlot}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bulk Inspection Awareness Section
// ---------------------------------------------------------------------------

/**
 * @component BulkInspectionSection
 *
 * Purpose:
 *   Renders the "Allow Bulk Inspections" toggle and, when enabled, an
 *   awareness summary of items that will NOT be populated during bulk.
 *
 * Props:
 *   - template: current template record.
 *   - forms: forms belonging to this template (for analysis).
 *   - onSave: host local-first patch callback.
 *
 * Behavior:
 *   - Toggle emits { allowBulkInspection } patch.
 *   - When toggled ON and forms are available, runs analyzeBulkTemplateReadiness
 *     and displays informational items grouped by category.
 *
 * Data Source: local
 * Constraints: Does not persist directly; no API calls.
 * Performance: Analysis is memoized on forms + parameter identity.
 */
function BulkInspectionSection({
  template,
  forms,
  onSave,
}: {
  template: TemplateBuilderTemplate;
  forms?: ReadonlyArray<BulkAnalysisForm>;
  onSave: (patch: Partial<TemplateBuilderTemplate>) => void | Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [validationResult, setValidationResult] = useState<BulkDefaultsValidationResult | null>(null);
  const [validationExpanded, setValidationExpanded] = useState(false);
  const { toast: sectionToast, showToast: showSectionToast, dismissToast: dismissSectionToast } = useToastNotification();
  const enabled = template.allowBulkInspection === true;

  // Stable key derived from form identity, structure, AND default values so
  // effects/memos re-fire when field defaults change (not just field counts).
  const formsKey = useMemo(
    () => forms?.map((f) => {
      const id = f._id ?? '';
      const fc = f.fields?.length ?? 0;
      const rc = f.fields?.filter((fld) => fld.required).length ?? 0;
      const dc = f.fields?.map((fld) => {
        const dv = fld.defaultValue ?? '';
        const ds = fld.extras?.defaultState ?? '';
        return `${dv}|${ds}`;
      }).join(';') ?? '';
      return `${id}:${fc}:${rc}:${dc}`;
    }).join(',') ?? '',
    [forms],
  );

  // Auto-evaluate bulk defaults when bulk inspections are toggled ON,
  // when forms change while the toggle is already ON, or when the template
  // is re-published (draft → published) with allowBulkInspection enabled.
  useEffect(() => {
    if (!enabled || !forms || forms.length === 0) {
      setValidationResult(null);
      return;
    }
    showSectionToast('info', 'Evaluating template...');
    // Slight delay so the toast is visible before the synchronous computation.
    const t = setTimeout(() => {
      const result = validateBulkDefaults(forms);
      setValidationResult(result);
      setValidationExpanded(!result.pass);
      dismissSectionToast();
    }, 150);
    return () => { clearTimeout(t); dismissSectionToast(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formsKey is a stable proxy for forms; template.status triggers re-evaluate on publish
  }, [enabled, formsKey, template.status]);

  const analysis: BulkTemplateAnalysisResult | null = useMemo(() => {
    if (!enabled || !forms || forms.length === 0) return null;
    return analyzeBulkTemplateReadiness(forms, template.parameters);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formsKey is a stable proxy for forms
  }, [enabled, formsKey, template.parameters]);

  const hasItems = analysis !== null && analysis.items.length > 0;

  return (
    <div className="col-span-3 bg-gray-50 rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Bulk Inspections</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Allow bulk inspections</span>
          <button
            type="button"
            role="switch"
            aria-label="Allow bulk inspections"
            aria-checked={enabled}
            onClick={() => onSave({ allowBulkInspection: !enabled })}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer shrink-0 ${
              enabled ? 'bg-orange-600' : 'bg-gray-300'
            }`}
          >
            <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-4' : 'translate-x-0.5'
            }`} />
          </button>
        </div>
      </div>

      {!enabled && (
        <p className="text-xs text-gray-400">
          Enable to allow this template to be used for bulk (multi-parcel) inspections.
        </p>
      )}

      {enabled && !hasItems && (
        <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
          <Check size={12} /> All fields are fully processable in bulk mode.
        </p>
      )}

      {enabled && hasItems && analysis && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-gray-600">{analysis.summary}</p>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-(--brand-primary) hover:underline flex items-center gap-1"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? 'Hide details' : 'Show details'}
          </button>

          {expanded && (
            <div className="space-y-3 pt-1">
              <AwarenessGroup
                title="Items skipped during bulk"
                items={analysis.items.filter((i) => i.category === 'skippedField' || i.category === 'skippedModule')}
              />
              <AwarenessGroup
                title="Required fields without defaults"
                items={analysis.items.filter((i) => i.category === 'requiredNoDefault')}
              />
              <AwarenessGroup
                title="Parameters not enforced"
                items={analysis.items.filter((i) => i.category === 'parameterNote')}
              />
            </div>
          )}
        </div>
      )}

      <ToastNotification toast={sectionToast} onDismiss={dismissSectionToast} />

      {enabled && forms && forms.length > 0 && validationResult && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <span className={`text-xs flex items-center gap-1 ${
              validationResult.pass ? 'text-green-600' : 'text-amber-600'
            }`}>
              {validationResult.pass
                ? <><CheckCircle2 size={12} /> All defaults validated</>
                : <><XCircle size={12} /> {validationResult.warnings.length} warning{validationResult.warnings.length === 1 ? '' : 's'} found</>
              }
            </span>
          </div>
          {validationResult && !validationResult.pass && (
            <div className="mt-2">
              <p className="text-xs text-gray-600 mb-1">{validationResult.summary}</p>
              <button
                type="button"
                onClick={() => setValidationExpanded(!validationExpanded)}
                className="text-xs text-(--brand-primary) hover:underline flex items-center gap-1"
              >
                {validationExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {validationExpanded ? 'Hide validation details' : 'Show validation details'}
              </button>
              {validationExpanded && (
                <ul className="mt-1 space-y-1">
                  {validationResult.warnings.map((warning, idx) => (
                    <li key={idx} className="flex items-start gap-1.5 text-xs">
                      {warning.severity === 'error'
                        ? <XCircle size={12} className="text-red-500 shrink-0 mt-0.5" />
                        : <AlertTriangle size={12} className="text-amber-500 shrink-0 mt-0.5" />
                      }
                      <span className={warning.severity === 'error' ? 'text-red-700' : 'text-amber-700'}>
                        {warning.formName && <span className="font-medium">{warning.formName}: </span>}
                        {warning.message}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {validationResult && validationResult.pass && (
            <p className="mt-1 text-xs text-green-600">{validationResult.summary}</p>
          )}
        </div>
      )}
    </div>
  );
}

function AwarenessGroup({ title, items }: { title: string; items: BulkAwarenessItem[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{title}</p>
      <ul className="space-y-1">
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-1.5 text-xs">
            {item.severity === 'warning' ? (
              <AlertTriangle size={12} className="text-amber-500 shrink-0 mt-0.5" />
            ) : (
              <Info size={12} className="text-blue-400 shrink-0 mt-0.5" />
            )}
            <span className={item.severity === 'warning' ? 'text-amber-700' : 'text-gray-600'}>
              {item.formName && <span className="font-medium">{item.formName}: </span>}
              {item.message}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * @contract TemplateAppearanceSectionProps
 *
 * Purpose:
 * - Supplies reusable template icon/color editing props.
 *
 * Fields:
 * - template: template whose display settings are edited.
 * - onSave: local-first patch callback.
 *
 * Rules:
 * - Empty icon selection clears custom display settings.
 *
 * Sync:
 * - Changes are emitted to the host callback for Dexie-first persistence.
 */
export interface TemplateAppearanceSectionProps {
  template: TemplateBuilderTemplate;
  onSave: (patch: Partial<TemplateBuilderTemplate>) => void | Promise<void>;
}

/**
 * @component TemplateAppearanceSection
 *
 * Purpose:
 * - Renders reusable custom icon and icon-color controls for template identity.
 *
 * Props:
 * - template: current template display settings.
 * - onSave: host local-first template patch callback.
 *
 * Behavior:
 * - Emits display patches when the user selects an icon or color.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not read or write persistence directly.
 *
 * Performance:
 * - Lightweight controlled inputs; no memoization required.
 */
export function TemplateAppearanceSection({ template, onSave }: TemplateAppearanceSectionProps) {
  const iconName = template.display?.icon || undefined;
  const iconColor = template.display?.iconColor || '';
  const updateDisplay = (patch: Partial<NonNullable<TemplateBuilderTemplate['display']>>) => {
    onSave({ display: { ...template.display, ...patch } });
  };

  return (
    <TemplateSection title="Appearance">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">Custom Icon</label>
          <IconPicker
            value={iconName}
            onChange={(name) => {
              if (name) updateDisplay({ icon: name });
              else onSave({ display: undefined });
            }}
            placeholder="Search icons..."
          />
          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
            <Info size={11} /> Leave empty to use the default type icon.
          </p>
        </div>

        {iconName && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Icon Color</label>
            <div className="flex items-center gap-1.5 flex-wrap">
              {ICON_COLOR_PRESETS.map((color) => (
                <button
                  key={color}
                  onClick={() => updateDisplay({ iconColor: color })}
                  className={`w-6 h-6 rounded-full border-2 transition-all ${iconColor === color ? 'border-gray-900 scale-110 ring-2 ring-gray-200' : 'border-transparent hover:border-gray-300'}`}
                  title={`Set icon color to ${color}`}
                  aria-label={`Icon color ${color}`}
                  style={{ backgroundColor: color }}
                />
              ))}
              <input
                type="text"
                value={iconColor}
                onChange={(event) => updateDisplay({ iconColor: event.target.value })}
                placeholder="#hex"
                className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
              />
            </div>
          </div>
        )}
      </div>
    </TemplateSection>
  );
}

function ParamToggle({ checked, onChange, label }: { checked: boolean; onChange: (value: boolean) => void; label: string }) {
  return (
    <label className="flex items-center justify-between py-1.5 cursor-pointer group">
      <span className="text-sm text-gray-700 group-hover:text-gray-900">{label}</span>
      <button
        type="button"
        role="switch"
        aria-label={label}
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
          checked ? 'bg-orange-600' : 'bg-gray-300'
        }`}
      >
        <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`} />
      </button>
    </label>
  );
}

/**
 * @contract TemplateParametersSectionProps
 *
 * Purpose:
 * - Supplies reusable template parameter toggle props.
 *
 * Fields:
 * - params: current template parameter bag.
 * - onChange: full parameter replacement callback.
 *
 * Rules:
 * - Undefined boolean values render as false where historical data may omit the field.
 *
 * Sync:
 * - Parameter changes are emitted to host code for Dexie-first persistence.
 */
export interface TemplateParametersSectionProps {
  params: TemplateParameters;
  onChange: (params: TemplateParameters) => void | Promise<void>;
}

/**
 * @component TemplateParametersSection
 *
 * Purpose:
 * - Renders reusable signature, permission, notification, and mobile parameter toggles.
 *
 * Props:
 * - params: current template parameter bag.
 * - onChange: parameter replacement callback.
 *
 * Behavior:
 * - Emits updated parameter objects when toggles change.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not persist directly.
 *
 * Performance:
 * - Controlled inputs only; caller owns debouncing/autosave behavior.
 */
export function TemplateParametersSection({ params, onChange }: TemplateParametersSectionProps) {
  const updateParams = (patch: Partial<TemplateParameters>) => onChange({ ...params, ...patch });

  return (
    <div className="space-y-3 row-span-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Parameters</h3>

      <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
        <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Responsible Party</h4>
        <ParamToggle checked={params.requireResponsibleParty || false} onChange={(value) => updateParams({ requireResponsibleParty: value })} label="Responsible party required" />
        <p className="text-[11px] text-gray-400 leading-snug -mt-0.5 mb-1">
          When on, the inspector must select a responsible party before a visit can be completed. When off, the property owner is used by default.
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
        <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Signatures</h4>
        <ParamToggle checked={params.requireInspectorSignature} onChange={(value) => updateParams({ requireInspectorSignature: value })} label="Inspector signature" />
        <ParamToggle checked={params.requireResponsiblePartySignature} onChange={(value) => updateParams({ requireResponsiblePartySignature: value })} label="Responsible party signature" />
      </div>

      <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
        <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Permissions</h4>
        <ParamToggle checked={params.enableSeverity || false} onChange={(value) => updateParams({ enableSeverity: value })} label="Severity levels" />
        <ParamToggle checked={params.allowExtensions || false} onChange={(value) => updateParams({ allowExtensions: value })} label="Allow extensions" />
        <ParamToggle checked={params.allowExceptions || false} onChange={(value) => updateParams({ allowExceptions: value })} label="Allow exceptions" />
        <ParamToggle checked={params.selfCertificationEnabled} onChange={(value) => updateParams({ selfCertificationEnabled: value })} label="Self-certification" />
      </div>

      <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
        <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Notifications & Mobile</h4>
        <ParamToggle checked={params.emailOwnerOnComplete} onChange={(value) => updateParams({ emailOwnerOnComplete: value })} label="Email owner on complete" />
        <ParamToggle checked={params.emailInspectorSummary} onChange={(value) => updateParams({ emailInspectorSummary: value })} label="Email inspector summary" />
        <ParamToggle checked={params.allowOfflineMode} onChange={(value) => updateParams({ allowOfflineMode: value })} label="Offline capable" />
      </div>
    </div>
  );
}

/**
 * @contract TemplateReinspectionSectionProps
 *
 * Purpose:
 * - Supplies reusable reinspection scheduling props.
 *
 * Fields:
 * - params: current template parameter bag.
 * - onChange: full parameter replacement callback.
 *
 * Rules:
 * - Auto-generated labels remain synchronized with the tier order.
 *
 * Sync:
 * - Schedule changes are emitted to host code for Dexie-first persistence.
 */
export interface TemplateReinspectionSectionProps {
  params: TemplateParameters;
  onChange: (params: TemplateParameters) => void | Promise<void>;
}

/**
 * @component TemplateReinspectionSection
 *
 * Purpose:
 * - Renders reusable auto-reinspection and tiered due-date controls.
 *
 * Props:
 * - params: current template parameters.
 * - onChange: parameter replacement callback.
 *
 * Behavior:
 * - Creates default 30/60/90-day tiers and keeps generated labels aligned with tier order.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not persist directly.
 *
 * Performance:
 * - Local editing flag only; persistence batching belongs to the host page/service.
 */
export function TemplateReinspectionSection({ params, onChange }: TemplateReinspectionSectionProps) {
  const [reinspEditing, setReinspEditing] = useState<boolean>(() => (params.reinspectionSchedule ?? []).length === 0);
  const updateParams = (patch: Partial<TemplateParameters>) => onChange({ ...params, ...patch });

  const ordinalSuffix = (n: number) => {
    const value = n % 100;
    if (value >= 11 && value <= 13) return 'th';
    switch (n % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
  };
  const autoLabelFor = (n: number, total: number) =>
    total > 1 && n === total ? 'Final Reinspection' : `${n}${ordinalSuffix(n)} Reinspection`;
  const autoLabelPattern = /^(\d+)(st|nd|rd|th) Reinspection$/;
  const isAutoLabel = (label?: string) =>
    !label || label === 'Final Reinspection' || autoLabelPattern.test(label);

  const resyncAutoLabels = (schedule: ReinspectionScheduleEntry[]): ReinspectionScheduleEntry[] => {
    const total = schedule.length;
    return schedule.map((entry, index) => {
      const reinspectionNumber = index + 1;
      const nextLabel = isAutoLabel(entry.label) ? autoLabelFor(reinspectionNumber, total) : entry.label;
      return { ...entry, reinspectionNumber, label: nextLabel };
    });
  };

  const handleScheduleChange = (index: number, field: keyof ReinspectionScheduleEntry, value: any) => {
    const schedule = [...(params.reinspectionSchedule || [])];
    schedule[index] = { ...schedule[index], [field]: value };
    updateParams({ reinspectionSchedule: schedule });
  };

  const addScheduleTier = () => {
    const base = [...(params.reinspectionSchedule || [])];
    base.push({ reinspectionNumber: base.length + 1, dueDays: 30, label: '' });
    updateParams({ reinspectionSchedule: resyncAutoLabels(base) });
  };

  const removeScheduleTier = (index: number) => {
    const base = (params.reinspectionSchedule || []).filter((_, i) => i !== index);
    updateParams({ reinspectionSchedule: resyncAutoLabels(base) });
  };

  const startReinspectionSetup = () => {
    const defaults: ReinspectionScheduleEntry[] = [
      { reinspectionNumber: 1, dueDays: 30, label: '' },
      { reinspectionNumber: 2, dueDays: 60, label: '' },
      { reinspectionNumber: 3, dueDays: 90, label: '' },
    ];
    updateParams({
      maxReinspections: params.maxReinspections ?? 3,
      reinspectionSchedule: resyncAutoLabels(defaults),
    });
    setReinspEditing(true);
  };

  return (
    <div className="col-span-2 bg-gray-50 rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Reinspection</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Auto-create on violations</span>
          <button
            type="button"
            role="switch"
            aria-label="Auto-create reinspection on violations"
            aria-checked={params.autoCreateReinspectionWhenViolations}
            onClick={() => updateParams({ autoCreateReinspectionWhenViolations: !params.autoCreateReinspectionWhenViolations })}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer shrink-0 ${params.autoCreateReinspectionWhenViolations ? 'bg-orange-600' : 'bg-gray-300'}`}
            title={params.autoCreateReinspectionWhenViolations ? 'Disable auto-reinspection' : 'Enable auto-reinspection'}
          >
            <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${params.autoCreateReinspectionWhenViolations ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </div>

      {params.autoCreateReinspectionWhenViolations ? (
        (params.reinspectionSchedule ?? []).length === 0 ? (
          <div className="flex items-center justify-between gap-3 py-2">
            <p className="text-xs text-gray-500">
              No reinspection schedule yet. Start setup to add a default 30/60/90-day schedule with auto-labels.
            </p>
            <button
              type="button"
              onClick={startReinspectionSetup}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-sm transition-colors"
            >
              <Plus size={12} /> Start Setup
            </button>
          </div>
        ) : reinspEditing ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap">Max:</label>
              <input
                type="number"
                min={1}
                max={10}
                value={params.maxReinspections || 3}
                aria-label="Maximum reinspections"
                onChange={(event) => updateParams({ maxReinspections: parseInt(event.target.value) || 3 })}
                className="w-14 px-2 py-0.5 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
              />
              <span className="text-xs text-gray-400">reinspections</span>
            </div>
            <div className="border border-gray-200 rounded-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-white">
                  <tr>
                    <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-400 w-6">#</th>
                    <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-400 w-20">Days due</th>
                    <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-400">Label</th>
                    <th className="px-1 py-1 w-6"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(params.reinspectionSchedule || []).map((entry, index) => (
                    <tr key={index}>
                      <td className="px-2 py-1 text-gray-400">{entry.reinspectionNumber}</td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          min={1}
                          value={entry.dueDays}
                          aria-label={`Due days for reinspection ${index + 1}`}
                          onChange={(event) => handleScheduleChange(index, 'dueDays', parseInt(event.target.value) || 1)}
                          className="w-16 px-1.5 py-0.5 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="text"
                          value={entry.label}
                          aria-label={`Label for reinspection ${index + 1}`}
                          onChange={(event) => handleScheduleChange(index, 'label', event.target.value)}
                          placeholder={autoLabelFor(index + 1, (params.reinspectionSchedule || []).length)}
                          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <button onClick={() => removeScheduleTier(index)} title="Remove schedule tier" className="text-gray-300 hover:text-red-500 transition-colors">
                          <X size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between pt-1">
              <button
                onClick={addScheduleTier}
                className="text-xs text-(--brand-primary) hover:underline flex items-center gap-1"
              >
                <Plus size={10} /> Add tier
              </button>
              <button
                type="button"
                onClick={() => setReinspEditing(false)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-sm transition-colors"
              >
                <Check size={12} /> Finish Setup
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-gray-500">
                Max <strong className="text-gray-700">{params.maxReinspections || 3}</strong> reinspections
              </p>
              <button
                type="button"
                onClick={() => setReinspEditing(true)}
                className="text-xs text-(--brand-primary) hover:underline inline-flex items-center gap-1"
              >
                <Pencil size={10} /> Edit
              </button>
            </div>
            <ul className="text-xs divide-y divide-gray-200 border border-gray-200 rounded-sm bg-white">
              {(params.reinspectionSchedule || []).map((entry) => (
                <li key={entry.reinspectionNumber} className="flex items-center gap-3 px-3 py-1.5">
                  <span className="text-gray-400 w-5">#{entry.reinspectionNumber}</span>
                  <span className="text-gray-500 w-20">{entry.dueDays} days</span>
                  <span className="text-gray-800 flex-1 truncate">{entry.label || autoLabelFor(entry.reinspectionNumber, (params.reinspectionSchedule || []).length)}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      ) : (
        <p className="text-xs text-gray-400">Enable above to configure the reinspection schedule.</p>
      )}
    </div>
  );
}

/**
 * @contract TemplateLibrarySettingsSectionProps
 *
 * Purpose:
 * - Supplies reusable system library visibility and discovery tag props.
 *
 * Fields:
 * - template: current system template.
 * - onSave: local-first template patch callback.
 *
 * Rules:
 * - Tags are lower-cased and deduplicated before emitting a patch.
 *
 * Sync:
 * - Library settings changes are emitted to host code for Dexie-first persistence.
 */
export interface TemplateLibrarySettingsSectionProps {
  template: TemplateBuilderTemplate;
  onSave: (patch: Partial<TemplateBuilderTemplate>) => void | Promise<void>;
}

/**
 * @component TemplateLibrarySettingsSection
 *
 * Purpose:
 * - Renders reusable system library visibility and tag controls.
 *
 * Props:
 * - template: current template record.
 * - onSave: host local-first patch callback.
 *
 * Behavior:
 * - Emits librarySettings patches for visibility and tag edits.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - System-web composes this slot; tenant-web omits it.
 *
 * Performance:
 * - Maintains only transient tag input state.
 */
export function TemplateLibrarySettingsSection({ template, onSave }: TemplateLibrarySettingsSectionProps) {
  const [tagInput, setTagInput] = useState('');
  const libSettings = ((template as { librarySettings?: TemplateLibrarySettings }).librarySettings) ?? { isInLibrary: true, visibility: 'public', tags: [] };
  const tags: string[] = libSettings.tags ?? [];

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (!tag || tags.includes(tag)) return;
    onSave({ librarySettings: { ...libSettings, tags: [...tags, tag] } } as Partial<TemplateBuilderTemplate>);
    setTagInput('');
  };
  const removeTag = (tag: string) => {
    onSave({ librarySettings: { ...libSettings, tags: tags.filter((item) => item !== tag) } } as Partial<TemplateBuilderTemplate>);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-2">Library Settings</h3>
      <p className="text-xs text-gray-500">
        Control how this template appears in the tenant-facing library. Templates must be Published before departments can adopt them.
      </p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
        <div className="space-y-2">
          {[
            { value: 'public', label: 'Public', desc: 'All departments can browse and adopt this template.', icon: <Globe size={14} className="text-blue-500" /> },
            { value: 'org', label: 'Organization', desc: 'Visible only to departments within the same organization.', icon: <Building2 size={14} className="text-purple-500" /> },
            { value: 'private', label: 'Private', desc: 'Hidden from the department library (system-internal only).', icon: <Lock size={14} className="text-gray-400" /> },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onSave({ librarySettings: { ...libSettings, visibility: option.value } } as Partial<TemplateBuilderTemplate>)}
              className={`w-full flex items-start gap-3 border-2 rounded-lg px-4 py-3 text-left transition-colors ${
                libSettings.visibility === option.value ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <span className="mt-0.5 shrink-0">{option.icon}</span>
              <div>
                <div className="text-sm font-semibold text-gray-800">{option.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{option.desc}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <span className="flex items-center gap-1.5"><Tag size={13} /> Tags</span>
        </label>
        <p className="text-xs text-gray-400 mb-2">Tags help departments discover this template in search.</p>
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs rounded-full px-3 py-1">
              {tag}
              <button onClick={() => removeTag(tag)} className="ml-0.5 text-orange-400 hover:text-red-500">
                <X size={10} />
              </button>
            </span>
          ))}
          {tags.length === 0 && <p className="text-xs text-gray-400">No tags added.</p>}
        </div>
        <div className="flex items-center gap-2">
          <input
            value={tagInput}
            onChange={(event) => setTagInput(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addTag(); } }}
            placeholder="e.g. annual, fire-code, residential"
            className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-orange-500"
          />
          <button onClick={addTag} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 rounded-md">
            <Plus size={13} /> Add
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * @contract TemplateAiMetadataSectionProps
 *
 * Purpose:
 * - Supplies reusable system AI metadata display props.
 *
 * Fields:
 * - template: current template record.
 * - onSave: local-first template patch callback.
 *
 * Rules:
 * - Regenerate emits a marker into librarySettings for the host persistence flow.
 *
 * Sync:
 * - AI metadata refresh intent is emitted to host code and synchronized by existing local-first services.
 */
export interface TemplateAiMetadataSectionProps {
  template: TemplateBuilderTemplate;
  onSave: (patch: Partial<TemplateBuilderTemplate>) => void | Promise<void>;
}

/**
 * @component TemplateAiMetadataSection
 *
 * Purpose:
 * - Renders reusable AI description, tag, and suggestion metadata.
 *
 * Props:
 * - template: current template record.
 * - onSave: host local-first patch callback.
 *
 * Behavior:
 * - Displays existing AI metadata or explanatory empty-state copy.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not call AI or API endpoints directly.
 *
 * Performance:
 * - Pure display section with one host callback for regeneration intent.
 */
export function TemplateAiMetadataSection({ template, onSave }: TemplateAiMetadataSectionProps) {
  const aiMetadata = template.aiMetadata;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b border-gray-100 pb-2">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <Sparkles size={14} className="text-purple-500" />
          AI Metadata
        </h3>
        {aiMetadata && (
          <button
            type="button"
            onClick={() => {
              const libSettings = ((template as { librarySettings?: TemplateLibrarySettings }).librarySettings) ?? {};
              onSave({ librarySettings: { ...libSettings, _aiRefresh: Date.now() } } as Partial<TemplateBuilderTemplate>);
            }}
            className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
          >
            <Sparkles size={11} /> Regenerate
          </button>
        )}
      </div>
      {aiMetadata ? (
        <div className="space-y-3">
          {aiMetadata.aiDescription && (
            <div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">AI Description</p>
              <p className="text-xs text-gray-700">{aiMetadata.aiDescription}</p>
            </div>
          )}
          {(aiMetadata.aiTags ?? []).length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">AI Tags</p>
              <div className="flex flex-wrap gap-1.5">
                {(aiMetadata.aiTags ?? []).map((tag) => (
                  <span key={tag} className="text-xs bg-purple-50 border border-purple-200 text-purple-700 px-2 py-0.5 rounded-full">{tag}</span>
                ))}
              </div>
            </div>
          )}
          {(aiMetadata.aiSuggestedFor ?? []).length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Suggested For</p>
              <div className="flex flex-wrap gap-1.5">
                {(aiMetadata.aiSuggestedFor ?? []).map((suggestion) => (
                  <span key={suggestion} className="text-xs bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 rounded-full">{suggestion}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2 p-3 rounded-lg bg-purple-50 border border-purple-100">
          <p className="text-xs text-purple-700">AI metadata is generated automatically when this template is published. It helps departments discover this template through search and recommendations.</p>
          <p className="text-[10px] text-purple-500">Generated fields: description · search tags · suggested use cases</p>
        </div>
      )}
    </div>
  );
}

export default TemplateGeneralTab;
